import React, { useState, useEffect, useCallback } from 'react'
import { ListRowsSkeleton } from '../components/PageSkeletons'
import { useNavigate } from 'react-router-dom'
import { retentionApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import {
  Trash2,
  RotateCcw,
  Loader2,
  FileText,
  Mail,
  Cpu,
  BarChart3,
  CreditCard,
  Webhook,
  Clock,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

const ENTITY_ICONS = {
  documents: FileText,
  requests: FileText,
  emails: Mail,
  async_jobs: Cpu,
  analysis_runs: BarChart3,
  webhook_deliveries: Webhook,
  metered_usage: CreditCard,
  notifications: FileText,
}

const ENTITY_LABELS = {
  documents: 'Document',
  requests: 'Request',
  emails: 'Email',
  async_jobs: 'Job',
  analysis_runs: 'Analysis Run',
  webhook_deliveries: 'Webhook Delivery',
  metered_usage: 'Usage Record',
  notifications: 'Notification',
}

export default function RecycleBin() {
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()
  const isReader = isReadOnlyUser(user)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [entityFilter, setEntityFilter] = useState('')
  const [restoringId, setRestoringId] = useState(null)

  const perPage = 20

  const loadItems = useCallback(async (p = 1, filter = entityFilter) => {
    setLoading(true)
    try {
      const params = { page: p, per_page: perPage }
      if (filter) params.entity_type = filter
      const res = await retentionApi.getRecycleBin(params)
      setItems(res.data.items || [])
      setTotal(res.data.total || 0)
      setPage(p)
    } catch (e) {
      toast.error('Failed to load recycle bin')
    } finally {
      setLoading(false)
    }
  }, [entityFilter])

  useEffect(() => {
    loadItems(1)
  }, [entityFilter])

  const handleRestore = async (itemId) => {
    if (isReader) { toast.error(READ_ONLY_MESSAGE); return }
    setRestoringId(itemId)
    try {
      await retentionApi.restoreItem(itemId)
      toast.success('Item restored successfully')
      loadItems(page)
    } catch (e) {
      toast.error('Failed to restore item')
    } finally {
      setRestoringId(null)
    }
  }

  const totalPages = Math.ceil(total / perPage)

  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return '-'
    const days = Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))
    if (days === 0) return 'Expiring today'
    if (days === 1) return '1 day left'
    return `${days} days left`
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-xl bg-red-50 dark:bg-red-900/20">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Recycle Bin</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Items deleted by retention policies. Restore within the grace period before permanent deletion.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Filter:</span>
        <button
          onClick={() => setEntityFilter('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
            !entityFilter
              ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
              : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 hover:border-gray-300'
          }`}>
          All ({total})
        </button>
        {Object.entries(ENTITY_LABELS).map(([key, label]) => (
          <button key={key}
            onClick={() => setEntityFilter(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              entityFilter === key
                ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 hover:border-gray-300'
            }`}>
            {label}s
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <ListRowsSkeleton />
      ) : items.length === 0 ? (
        <div className="py-20 text-center border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50/50 dark:bg-gray-900/50">
          <CheckCircle2 className="h-8 w-8 text-green-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Recycle bin is empty</p>
          <p className="text-xs text-gray-400 mt-1">No items have been deleted by retention policies yet.</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-900">
            {/* Header */}
            <div className="grid grid-cols-[1fr_120px_120px_120px_80px] gap-3 px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Item</span>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Type</span>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Deleted</span>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Expires In</span>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-right">Action</span>
            </div>
            {/* Rows */}
            {items.map(item => {
              const Icon = ENTITY_ICONS[item.entity_type] || FileText
              const daysLeft = Math.max(0, Math.ceil((new Date(item.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
              const isUrgent = daysLeft <= 3
              return (
                <div key={item.id} className="grid grid-cols-[1fr_120px_120px_120px_80px] gap-3 px-5 py-3 items-center border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors">
                  {/* Name */}
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Icon className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate font-medium">
                      {item.entity_name || `#${item.entity_id}`}
                    </span>
                  </div>
                  {/* Type */}
                  <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {ENTITY_LABELS[item.entity_type] || item.entity_type}
                  </span>
                  {/* Deleted date */}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '-'}
                  </span>
                  {/* Expires in */}
                  <div className="flex items-center gap-1.5">
                    {isUrgent ? (
                      <AlertTriangle className="h-3 w-3 text-red-500" />
                    ) : (
                      <Clock className="h-3 w-3 text-gray-400" />
                    )}
                    <span className={`text-xs font-medium ${isUrgent ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {formatTimeRemaining(item.expires_at)}
                    </span>
                  </div>
                  {/* Restore button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleRestore(item.id)}
                      disabled={isReader || restoringId === item.id}
                      title={isReader ? READ_ONLY_MESSAGE : undefined}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {restoringId === item.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3 w-3" />
                      )}
                      Restore
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <span className="text-xs text-gray-400">
                Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total} items
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => loadItems(page - 1)} disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors">
                  <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  {page} / {totalPages}
                </span>
                <button onClick={() => loadItems(page + 1)} disabled={page >= totalPages}
                  className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors">
                  <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Info footer */}
      <div className="mt-6 rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Items are kept in the recycle bin for <strong>30 days</strong> before permanent deletion. 
          Restoring an item marks it as recovered - however, associated blob files (if deleted) cannot be restored.
        </p>
      </div>
    </div>
  )
}
